import { Autocomplete, Checkbox, Divider, MenuItem, Paper, TextField, TextFieldProps, Tooltip } from '@mui/material';
import { useContext, useEffect, useRef, useState } from 'react';
import ApiService from '../ApiService';
import { Dealer } from '../types/Dealer';
import { getStyles } from './ToolbarStyles';
import { UserContext } from '../App';

type DealerFilter = (dealers: Dealer[] | null) => Dealer[] | null;

export interface SelectDealerProps {
    disabled?: boolean;
    dealerId: number | undefined | null;
    agencyId?: number | null;
    error?: boolean;
    dealerSelected(dealerId: number | null, dealer: Dealer | null): void;
    dealerDefaultSelect?: boolean;
    variant?: TextFieldProps['variant'];
    placeholder?: TextFieldProps['placeholder'];
    label?: string;
    showLabel?: boolean;
    required?: boolean;
    isHeader?: boolean;
    filter?(dealers: Dealer[] | null): Dealer[] | null;
    sx?: TextFieldProps['sx'];
    showInactiveAdvertisersToggle: boolean;
}

export default function DealerSelector(props: SelectDealerProps) {
    const { dealerSelected, dealerDefaultSelect, filter, showInactiveAdvertisersToggle } = props;

    const [dealers, setDealers] = useState<Dealer[] | null>(null);
    const [allDealers, setAllDealers] = useState<Dealer[] | null>(null);
    const [selectedDealer, setSelectedDealer] = useState<Dealer | null>(null);
    const [dealerDefaultSelected, setDealerDefaultSelected] = useState<boolean>(false);
    const [hideInactive, setHideInactive] = useState<boolean>(true);
    const { userContext } = useContext(UserContext);

    const filterRef = useRef<DealerFilter | undefined>(filter);

    useEffect(() => {
        if (props.dealerId && dealers) {
            let dealer = dealers.find((d) => {
                return d.id === props.dealerId;
            });
            setSelectedDealer(dealer ?? null);
        } else {
            if (dealerDefaultSelect === true && !dealerDefaultSelected && dealers && !selectedDealer) {
                let dealer = dealers[0];
                setSelectedDealer(dealer);
                dealerSelected(dealer?.id ?? null, dealer);
                setDealerDefaultSelected(true);
            } else {
                setSelectedDealer(null);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.dealerId, dealers]);

    useEffect(() => {
        if (props.agencyId) {
            ApiService.getAgencyDealers(props.agencyId)
                .then((response) => {
                    setAllDealers(filter ? filter(response.data) : response.data);
                    const activeDealers = response.data.filter((dealer: Dealer) => dealer.activeStatus);
                    setDealers(filter ? filter(activeDealers) : activeDealers);
                    if (!props.isHeader && activeDealers.length === 1) {
                        setSelectedDealer(activeDealers[0]);
                        dealerSelected(activeDealers[0].id ? activeDealers[0].id : null, activeDealers[0]);
                    }
                })
                .catch(() => {});
        } else {
            ApiService.getAssignedDealers()
                .then((response) => {
                    setAllDealers(filterRef.current ? filterRef.current(response.data) : response.data);
                    const activeDealers = response.data.filter((dealer: Dealer) => dealer.activeStatus);
                    setDealers(filterRef.current ? filterRef.current(activeDealers) : activeDealers);
                })
                .catch(() => {});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.agencyId, userContext, props.isHeader]);

    useEffect(() => {
        if (allDealers) {
            if (!showInactiveAdvertisersToggle || hideInactive) {
                const activeDealers = allDealers.filter((dealer: Dealer) => dealer.activeStatus);
                setDealers(activeDealers);
            } else {
                setDealers(allDealers);
            }
        }
    }, [hideInactive, allDealers, showInactiveAdvertisersToggle]);

    function setDealerId(id: string, dealer: Dealer | null) {
        if (id === 'undefined') {
            props.dealerSelected(null, null);
            setSelectedDealer(null);
        } else {
            props.dealerSelected(Number(id), dealer);
        }
    }

    function dealerChange(_event: any, newValue: Dealer | null) {
        setDealerId(String(newValue?.id), newValue);
    }

    if (!userContext.isAdmin() && dealers && dealers?.length < 2) {
        return null;
    }

    const sxProps = { ...getStyles(props.isHeader), ...props?.sx };

    return (
        <>
            <Autocomplete
                sx={sxProps}
                options={dealers ? dealers : []}
                getOptionLabel={(option) => option.dealerName}
                renderOption={(liProps, option) => {
                    if (!option || !option.dealerName) {
                        return null;
                    }

                    const isActive = option?.activeStatus === true;

                    return (
                        <li
                            {...liProps}
                            key={option.id}
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '8px 16px',
                            }}
                        >
                            <Tooltip title={option.dealerName} placement="top" arrow>
                                <span
                                    style={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        flex: 1,
                                        minWidth: 0,
                                    }}
                                >
                                    {option.dealerName}
                                </span>
                            </Tooltip>
                            {showInactiveAdvertisersToggle && (
                                <span
                                    style={{
                                        fontSize: '12px',
                                        padding: '2px 8px',
                                        borderRadius: '12px',
                                        backgroundColor: isActive ? '#e8f5e9' : '#ffebee',
                                        color: isActive ? '#2e7d32' : '#c62828',
                                        fontWeight: 500,
                                        marginLeft: '8px',
                                        flexShrink: 0,
                                    }}
                                >
                                    {isActive ? 'Active' : 'Inactive'}
                                </span>
                            )}
                        </li>
                    );
                }}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                size="small"
                fullWidth={false}
                value={selectedDealer}
                disabled={props.disabled || !dealers || (!props.isHeader && dealers.length === 1)}
                onChange={dealerChange}
                renderInput={(params) => (
                    <TextField
                        color={props.isHeader ? 'secondary' : 'primary'}
                        error={props.error}
                        required={props.required !== undefined ? props.required : !props.disabled}
                        variant={props.variant ? props.variant : 'outlined'}
                        {...params}
                        label={
                            props?.showLabel
                                ? props.label
                                : selectedDealer
                                ? ''
                                : props.disabled
                                ? 'Advertiser'
                                : props.label !== undefined
                                ? props.label
                                : 'Select an Existing Advertiser'
                        }
                        placeholder={props.placeholder}
                    />
                )}
                slots={{
                    paper: (paperProps) => (
                        <Paper
                            {...paperProps}
                            sx={{
                                boxShadow: 8,
                                borderRadius: '8px',
                            }}
                        >
                            {showInactiveAdvertisersToggle && (
                                <>
                                    <MenuItem
                                        onMouseDown={(event) => event.preventDefault()}
                                        sx={{
                                            px: 3,
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                        }}
                                    >
                                        <div
                                            onClick={() => setHideInactive(!hideInactive)}
                                            style={{
                                                cursor: 'pointer',
                                                userSelect: 'none',
                                                display: 'flex',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <Checkbox
                                                checked={hideInactive}
                                                size="small"
                                                sx={{ pointerEvents: 'none' }}
                                            />
                                            <span style={{ marginLeft: '0px' }}>Hide Inactive</span>
                                        </div>
                                    </MenuItem>
                                    <Divider />
                                </>
                            )}
                            {paperProps.children}
                        </Paper>
                    ),
                }}
            />
        </>
    );
}
